import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../../services/api';
import { isMongoObjectIdString } from './utils';

export type LivestreamStreamProgramEditDialogProps = {
    open: boolean;
    onClose: () => void;
    streamId: string;
    /** livestream_channels._id — required to list/create programs */
    channelId: string | null | undefined;
    channelName?: string;
    /** Current assignment on this stream (livestream_streams.livestreamProgramId) */
    assignedProgramId: string | null;
    assignedProgramName?: string;
    onSuccess: () => void;
};

const UNASSIGNED_SELECT_VALUE = '__none__';

const splitCsv = (s: string): string[] =>
    s
        .split(/[,;\n]+/)
        .map((x) => x.trim())
        .filter(Boolean);

const joinCsv = (items: string[] | undefined): string => (items ?? []).join(', ');

function programRowChannelMongoId(p: {
    channelId?: { _id?: string } | string;
}): string {
    const c = p.channelId;
    if (c == null) return '';
    if (typeof c === 'string') return c;
    if (typeof c === 'object' && '_id' in c && (c as { _id?: unknown })._id != null) {
        return String((c as { _id: unknown })._id);
    }
    const asString = String(c);
    return isMongoObjectIdString(asString) ? asString : '';
}

export const LivestreamStreamProgramEditDialog: React.FC<LivestreamStreamProgramEditDialogProps> = ({
    open,
    onClose,
    streamId,
    channelId,
    channelName,
    assignedProgramId,
    assignedProgramName,
    onSuccess,
}) => {
    const { t } = useTranslation('explorer');
    const queryClient = useQueryClient();
    const [selectedProgramId, setSelectedProgramId] = useState<string>(UNASSIGNED_SELECT_VALUE);
    const [createMode, setCreateMode] = useState(false);
    const [newName, setNewName] = useState('');
    const [newKeywords, setNewKeywords] = useState('');
    const [newHashtags, setNewHashtags] = useState('');
    const [editName, setEditName] = useState('');
    const [editKeywords, setEditKeywords] = useState('');
    const [editHashtags, setEditHashtags] = useState('');

    const resolvedChannelMongoId =
        channelId != null && String(channelId).trim() !== '' ? String(channelId).trim() : '';

    const { data: programsData, isLoading: programsLoading } = useQuery({
        queryKey: ['livestream-programs-dialog', resolvedChannelMongoId],
        queryFn: () => apiService.getLivestreamPrograms(resolvedChannelMongoId, false),
        enabled: open && isMongoObjectIdString(resolvedChannelMongoId),
    });

    const programs = useMemo(() => {
        const raw = programsData?.programs ?? [];
        if (!isMongoObjectIdString(resolvedChannelMongoId)) return [];
        return raw.filter((p) => {
            const rowChannelId = programRowChannelMongoId(p);
            return !rowChannelId || rowChannelId === resolvedChannelMongoId;
        });
    }, [programsData?.programs, resolvedChannelMongoId]);

    const programOptions = useMemo(() => {
        const sorted = [...programs].sort((a, b) => a.name.localeCompare(b.name, 'es'));
        const assignedId = assignedProgramId?.trim();
        if (
            assignedId &&
            !sorted.some((p) => String(p._id) === assignedId)
        ) {
            sorted.push({
                _id: assignedId,
                name: assignedProgramName?.trim() || 'Programa',
                channelId: {
                    _id: resolvedChannelMongoId,
                    channelName: channelName ?? '',
                    channelUrl: '',
                },
                keywords: [],
                hashtags: [],
                schedules: [],
                isActive: true,
                hidden: false,
                createdAt: '',
                updatedAt: '',
            });
            sorted.sort((a, b) => a.name.localeCompare(b.name, 'es'));
        }
        return sorted;
    }, [programs, assignedProgramId, assignedProgramName, resolvedChannelMongoId, channelName]);

    const selectedProgram = useMemo(
        () =>
            selectedProgramId !== UNASSIGNED_SELECT_VALUE
                ? programOptions.find((p) => String(p._id) === selectedProgramId)
                : undefined,
        [programOptions, selectedProgramId],
    );

    const programMetadataDirty = useMemo(() => {
        if (!selectedProgram) return false;
        return (
            editName.trim() !== selectedProgram.name.trim() ||
            joinCsv(splitCsv(editKeywords)) !== joinCsv(selectedProgram.keywords) ||
            joinCsv(splitCsv(editHashtags)) !== joinCsv(selectedProgram.hashtags)
        );
    }, [selectedProgram, editName, editKeywords, editHashtags]);

    useEffect(() => {
        if (!open) return;
        setCreateMode(false);
        setNewName('');
        setNewKeywords('');
        setNewHashtags('');
    }, [open]);

    useEffect(() => {
        if (!open || createMode) return;
        const ids = new Set(programOptions.map((p) => String(p._id)));
        if (assignedProgramId && ids.has(assignedProgramId)) {
            setSelectedProgramId(assignedProgramId);
        } else {
            setSelectedProgramId(UNASSIGNED_SELECT_VALUE);
        }
    }, [open, assignedProgramId, programOptions, createMode]);

    useEffect(() => {
        if (!open || createMode || !selectedProgram) {
            setEditName('');
            setEditKeywords('');
            setEditHashtags('');
            return;
        }
        setEditName(selectedProgram.name);
        setEditKeywords(joinCsv(selectedProgram.keywords));
        setEditHashtags(joinCsv(selectedProgram.hashtags));
    }, [open, createMode, selectedProgram]);

    const invalidateExplorer = () => {
        queryClient.invalidateQueries({ queryKey: ['livestream-programs-dialog'] });
        queryClient.invalidateQueries({ queryKey: ['explorer-livestream-program'] });
        queryClient.invalidateQueries({ queryKey: ['explorer-livestreams'] });
        queryClient.invalidateQueries({ queryKey: ['explorer-livestream-channel'] });
    };

    const patchMutation = useMutation({
        mutationFn: (livestreamProgramId: string | null) =>
            apiService.patchLivestreamStreamProgram(streamId, { livestreamProgramId }),
        onSuccess: () => {
            invalidateExplorer();
            onSuccess();
            onClose();
        },
    });

    const updateProgramMutation = useMutation({
        mutationFn: async (programId: string) => {
            const name = editName.trim();
            if (!name) throw new Error(t('programNameRequired'));
            await apiService.updateLivestreamProgram(programId, {
                name,
                keywords: splitCsv(editKeywords),
                hashtags: splitCsv(editHashtags),
            });
        },
        onSuccess: () => {
            invalidateExplorer();
        },
    });

    const createThenAssignMutation = useMutation({
        mutationFn: async () => {
            const name = newName.trim();
            if (!name || !isMongoObjectIdString(resolvedChannelMongoId)) throw new Error(t('nameAndChannelRequired'));
            const { program } = await apiService.createLivestreamProgram({
                channelId: resolvedChannelMongoId,
                name,
                keywords: splitCsv(newKeywords),
                hashtags: splitCsv(newHashtags),
                isActive: true,
            });
            const pid = program?._id != null ? String(program._id) : null;
            if (!pid) throw new Error(t('createdProgramIdMissing'));
            await apiService.patchLivestreamStreamProgram(streamId, { livestreamProgramId: pid });
        },
        onSuccess: () => {
            invalidateExplorer();
            onSuccess();
            onClose();
        },
    });

    const busy =
        patchMutation.isPending ||
        createThenAssignMutation.isPending ||
        updateProgramMutation.isPending;

    const resolveAssignmentId = (): string | null => {
        if (selectedProgramId === UNASSIGNED_SELECT_VALUE) return null;
        return selectedProgramId;
    };

    const handleSave = async () => {
        if (createMode) {
            createThenAssignMutation.mutate();
            return;
        }

        const assignmentId = resolveAssignmentId();
        if (assignmentId && programMetadataDirty) {
            try {
                await updateProgramMutation.mutateAsync(assignmentId);
            } catch {
                return;
            }
        }

        const currentAssigned = assignedProgramId?.trim() || null;
        const assignmentChanged = assignmentId !== currentAssigned;
        if (assignmentChanged) {
            patchMutation.mutate(assignmentId);
            return;
        }

        if (assignmentId && programMetadataDirty) {
            invalidateExplorer();
            onSuccess();
            onClose();
            return;
        }
    };

    const handleClear = () => {
        setSelectedProgramId(UNASSIGNED_SELECT_VALUE);
        patchMutation.mutate(null);
    };

    const noChannel = !isMongoObjectIdString(resolvedChannelMongoId);

    const hasChanges = useMemo(() => {
        if (createMode) return Boolean(newName.trim());
        const nextAssignment =
            selectedProgramId === UNASSIGNED_SELECT_VALUE ? null : selectedProgramId || null;
        const currentAssignment = assignedProgramId?.trim() || null;
        return nextAssignment !== currentAssignment || programMetadataDirty;
    }, [
        createMode,
        newName,
        selectedProgramId,
        assignedProgramId,
        programMetadataDirty,
    ]);

    const canSave = !noChannel && hasChanges;

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('livestreamProgramTitle')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {channelName ? (
                        <Typography variant="body2" color="text.secondary">
                            {t('channelLabel', { name: channelName })}
                        </Typography>
                    ) : null}
                    <Typography variant="body2">
                        {t('currentAssignment')}{' '}
                        <strong>
                            {assignedProgramName?.trim() ||
                                (assignedProgramId ? t('program') : t('noProgram'))}
                        </strong>
                    </Typography>
                    {noChannel ? (
                        <Alert severity="warning">
                            {t('channelUnresolved')}
                        </Alert>
                    ) : null}
                    {!noChannel && programsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : null}
                    {!noChannel && !programsLoading && !createMode ? (
                        <FormControl fullWidth size="small">
                            <InputLabel id="prog-select-label">{t('program')}</InputLabel>
                            <Select
                                labelId="prog-select-label"
                                label={t('program')}
                                value={selectedProgramId}
                                onChange={(e) => setSelectedProgramId(e.target.value as string)}
                            >
                                <MenuItem value={UNASSIGNED_SELECT_VALUE}>
                                    <em>{t('noProgram')}</em>
                                </MenuItem>
                                {programOptions.map((p) => (
                                    <MenuItem key={String(p._id)} value={String(p._id)}>
                                        {p.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : null}
                    {!noChannel && !programsLoading && !createMode && selectedProgram ? (
                        <>
                            <Divider />
                            <Typography variant="subtitle2">{t('editSelectedProgram')}</Typography>
                            <TextField
                                label={t('programName')}
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                fullWidth
                                required
                                size="small"
                            />
                            <TextField
                                label={t('keywordsComma')}
                                value={editKeywords}
                                onChange={(e) => setEditKeywords(e.target.value)}
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label={t('hashtagsComma')}
                                value={editHashtags}
                                onChange={(e) => setEditHashtags(e.target.value)}
                                fullWidth
                                size="small"
                            />
                        </>
                    ) : null}
                    {!noChannel && createMode ? (
                        <>
                            <TextField
                                label={t('programName')}
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                fullWidth
                                required
                                size="small"
                            />
                            <TextField
                                label={t('keywordsComma')}
                                value={newKeywords}
                                onChange={(e) => setNewKeywords(e.target.value)}
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label={t('hashtagsComma')}
                                value={newHashtags}
                                onChange={(e) => setNewHashtags(e.target.value)}
                                fullWidth
                                size="small"
                            />
                        </>
                    ) : null}
                    {!noChannel && !programsLoading ? (
                        <Button
                            size="small"
                            onClick={() => setCreateMode(!createMode)}
                            disabled={busy}
                        >
                            {createMode ? t('chooseExistingProgram') : t('createNewProgram')}
                        </Button>
                    ) : null}
                    {(patchMutation.isError ||
                        createThenAssignMutation.isError ||
                        updateProgramMutation.isError) && (
                        <Alert severity="error">
                            {String(
                                (patchMutation.error as any)?.response?.data?.message ||
                                    (createThenAssignMutation.error as any)?.response?.data?.message ||
                                    (updateProgramMutation.error as any)?.response?.data?.message ||
                                    (patchMutation.error as Error)?.message ||
                                    (createThenAssignMutation.error as Error)?.message ||
                                    (updateProgramMutation.error as Error)?.message ||
                                    'Error',
                            )}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={busy}>
                    {t('cancel')}
                </Button>
                <Button color="warning" onClick={handleClear} disabled={busy || noChannel}>
                    {t('removeProgram')}
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={busy || !canSave}>
                    {busy ? <CircularProgress size={22} color="inherit" /> : t('save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LivestreamStreamProgramEditDialog;
